import { useScreenAvailable } from '@api/flows';
import CodeSnippet, {
	LangTypeBackend,
	LangTypeFrontend
} from '@components/CodeEditor/CodeSnippet';
import { CODE_EDITOR_SUPPORTED_LANG } from '@components/CodeEditor/types';
import Loader from '@components/Loader/Loader';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import {
	Alert,
	Autocomplete,
	Box,
	Button,
	Divider,
	Grid,
	IconButton,
	Link,
	TextField,
	ThemeProvider,
	Tooltip,
	Typography
} from '@mui/material';
import getLightTheme from 'constants/Theme/LightTheme';
import jwt from 'jwt-decode';
import WizardBackButton from 'pages/GettingStarted/Wizard/components/WizardBackLink';
import WizardContentLayout from 'pages/GettingStarted/Wizard/Phases/WizardContentLayout';
import {
	isWizardSSOSelected,
	WIZARD_PAGES
} from 'pages/GettingStarted/Wizard/utils/consts';
import React, { FC, useMemo } from 'react';
import { ArchType, IWizardProps, WizardPhase } from 'types/wizard';
import { useLocalStorage } from 'usehooks-ts';
import { useWizardUpdate } from '../../utils/hook';
import DescopeFlowPreview from './DescopeFlowPreview';

const WizardPreviewPage: FC<IWizardProps> = ({ state }) => {
	const { sampleApp, archType, projectId, flows, combinations } = state;
	const { update } = useWizardUpdate(state);

	const [wizardPreviewInProgress, setWizardPreviewInProgress] = useLocalStorage(
		'wizardPreviewInProgress',
		false
	);

	const readyFlows = flows.filter((flow) => flow.id !== 'step-up'); // we don't want to show step up here
	const [selectedFlow, setSelectedFlow] = React.useState(
		readyFlows.filter((flow) => flow.id === 'sign-up-or-in')[0] || // we want to start with "sign up or in"
			readyFlows[0] ||
			{}
	);

	const [clearInProgress, setClearInProgress] = React.useState(false);
	const [successLogin, setSuccessLogin] = React.useState(false);

	const [loginDetails, setLoginDetails] = React.useState<
		{ sessionJwt: string } | undefined
	>(undefined);

	const [runCodeClicked, setRunCodeClicked] = React.useState(
		wizardPreviewInProgress
	);

	const { isFetching: runCodeLoading } = useScreenAvailable(
		'sign-up-or-in',
		runCodeClicked
	);
	const shouldDisplayRunCode = !runCodeClicked || runCodeLoading;

	const handleNext = React.useCallback(() => {
		update({
			page: WIZARD_PAGES.checklist,
			phase: WizardPhase.Customize
		});
	}, [update]);

	const handleBack = React.useCallback(() => {
		update({
			page: WIZARD_PAGES.apply,
			phase: WizardPhase.Build
		});
	}, [update]);

	const clearLoginSession = React.useCallback(() => {
		setClearInProgress(true);
		setWizardPreviewInProgress(true);
		setSuccessLogin(false);
		setLoginDetails(undefined);
		localStorage.removeItem('DS');
		setTimeout(() => {
			setClearInProgress(false);
		});
	}, [setWizardPreviewInProgress]);

	const { sessionJwtHeader, sessionJwtPayload } = useMemo<{
		sessionJwtHeader: string;
		sessionJwtPayload: string;
	}>(() => {
		let header = '';
		let payload = '';
		if (loginDetails) {
			try {
				header = JSON.stringify(
					jwt(loginDetails.sessionJwt, {
						header: true
					}),
					undefined,
					2
				);
				payload = JSON.stringify(
					jwt(loginDetails.sessionJwt, {
						header: false
					}),
					undefined,
					2
				);
			} catch (ex) {
				// do nothing
			}
		}
		return { sessionJwtHeader: header, sessionJwtPayload: payload };
	}, [loginDetails]);

	const lightTheme = getLightTheme();

	return (
		<WizardContentLayout
			{...{ state }}
			actionButton={
				<Button
					data-testid="wizard-skip-button"
					onClick={handleNext}
					endIcon={<ArrowForwardIcon />}
				>
					<Typography variant="body1">
						{intl.tr('wizard.phase.integrate.preview.customize.button')}
					</Typography>
				</Button>
			}
			backButton={<WizardBackButton onClick={handleBack} />}
			slideStyle={{}}
		>
			<Box
				display="flex"
				flexDirection={{
					xs: 'column',
					sm: 'row'
				}}
			>
				<Box display="flex" flexDirection="column" flexGrow={1}>
					<Box sx={{ paddingRight: '5%' }}>
						<Typography variant="h6">
							{intl.tr('wizard.phase.integrate.preview.header')}
						</Typography>
						<Typography variant="body1" sx={{ marginTop: '6px' }}>
							{intl.tr('wizard.phase.integrate.preview.explore')}
						</Typography>
						<Typography variant="body1">
							<Link
								target="_blank"
								href="https://docs.descope.com/build/guides/gettingstarted"
								underline="hover"
							>
								{intl.tr('wizard.phase.integrate.preview.quickGuide')}
							</Link>
						</Typography>
						{archType === ArchType.MT && isWizardSSOSelected(combinations) && (
							<Box mt={3}>
								<Alert severity="warning">
									<Typography variant="body2">
										{intl.tr('wizard.phase.integrate.preview.sso.alert')}
									</Typography>
								</Alert>
							</Box>
						)}
						<Box
							display="flex"
							flexDirection="row"
							sx={{ marginTop: '40px', marginBottom: '8px' }}
						>
							<Typography variant="body1">
								{intl.tr('wizard.phase.integrate.preview.frontend', {
									flow: selectedFlow.title
								})}
								<Link
									target="_blank"
									href="https://docs.descope.com/build/guides/gettingstarted/#integrate-your-frontend"
									sx={{ marginLeft: '8px' }}
									underline="hover"
								>
									{intl.tr('wizard.phase.integrate.preview.readMore')}
								</Link>
							</Typography>
						</Box>
						<Box>
							<CodeSnippet
								flowId={selectedFlow.id}
								projectId={projectId}
								langType={LangTypeFrontend}
							/>
						</Box>
						<Box sx={{ marginTop: '32px', marginBottom: '26px' }}>
							<Divider />
						</Box>
						<Box
							display="flex"
							flexDirection="row"
							sx={{ marginBottom: '8px' }}
						>
							<Typography variant="body1">
								{intl.tr('wizard.phase.integrate.preview.backend')}
								<Link
									target="_blank"
									href="https://docs.descope.com/build/guides/session"
									sx={{ marginLeft: '8px' }}
									underline="hover"
								>
									{intl.tr('wizard.phase.integrate.preview.readMore')}
								</Link>
							</Typography>
						</Box>
						<Box>
							<CodeSnippet
								flowId={selectedFlow.id}
								projectId={projectId}
								langType={LangTypeBackend}
							/>
						</Box>
					</Box>
				</Box>
				<Box
					display="flex"
					flexDirection="column"
					flexGrow={1}
					pt={{
						xs: 6,
						sm: 1
					}}
					alignItems="center"
				>
					<Box
						width="300px"
						display="flex"
						flexDirection="row"
						alignItems="center"
					>
						<Typography mr={1}>
							{intl.tr('wizard.phase.integrate.preview.flow.selector')}
						</Typography>
						<Autocomplete
							filterSelectedOptions={false}
							data-testid="wizard-preview-flow-picker"
							value={selectedFlow}
							isOptionEqualToValue={(option, value) => option.id === value.id}
							options={readyFlows || []}
							getOptionLabel={(option) => option.title}
							onChange={(_, v) => {
								setSelectedFlow(v!);
								if (successLogin) {
									clearLoginSession();
								}
							}}
							clearIcon={null}
							renderInput={(params) => (
								<Box>
									<TextField
										data-testid="wizard-preview-flow-picker-input"
										{...params}
									/>
								</Box>
							)}
						/>
						<Box ml={1}>
							<IconButton
								data-testid="wizard-flow-restart-button"
								aria-label={intl.tr('wizard.phase.integrate.preview.restart')}
								edge="end"
								onClick={clearLoginSession}
							>
								<Tooltip
									title={intl.tr('wizard.phase.integrate.preview.restart')}
								>
									<RestartAltIcon />
								</Tooltip>
							</IconButton>
						</Box>
					</Box>
					<ThemeProvider theme={lightTheme}>
						<Box
							display="flex"
							flexDirection={{
								xs: 'column',
								sm: 'row'
							}}
							width="100%"
						>
							<Grid container columns={5}>
								<Grid item xs={0} md={1} />
								<Grid item xs md={3}>
									<Box
										display="flex"
										alignItems="center"
										justifyContent="center"
										sx={{
											borderRadius: '16px',
											overflow: successLogin ? 'scroll' : 'hidden',
											padding: '32px 16px 0 16px',
											paddingBottom: successLogin ? '32px' : undefined,
											minHeight: '500px',
											backgroundColor: lightTheme.palette.wizard.card.main,
											marginTop: '16px',
											marginBottom: '16px'
										}}
										boxShadow="0px 8px 9px -5px rgba(0, 0, 0, 0.2), 0px 15px 22px 2px rgba(0, 0, 0, 0.14), 0px 6px 28px 5px rgba(0, 0, 0, 0.12)"
									>
										{shouldDisplayRunCode && (
											<Box
												textAlign="center"
												data-testid="wizard-preview-run-code-container"
											>
												{runCodeLoading ? (
													<Loader
														data-testid="wizard-preview-flow-loader"
														isLoading
														variant="circular"
														circularLoaderSize={40}
													/>
												) : (
													<Button
														variant="contained"
														color="primary"
														size="large"
														onClick={() => {
															setWizardPreviewInProgress(true);
															setRunCodeClicked(true);
														}}
													>
														{intl.tr('wizard.phase.integrate.preview.runCode')}
													</Button>
												)}
											</Box>
										)}
										{!shouldDisplayRunCode &&
											(successLogin ? (
												<Box
													display="flex"
													flexDirection="column"
													alignItems="center"
													data-testid="wizard-success-login"
													width="100%"
												>
													<Typography fontSize="70px">
														<CheckCircleIcon
															color="success"
															fontSize="inherit"
														/>
													</Typography>
													<Typography
														variant="h6"
														color={lightTheme.palette.grey[900]}
														textAlign="center"
														sx={{ marginTop: '16px', marginBottom: '24px' }}
													>
														{intl.tr(
															'wizard.phase.integrate.preview.auth.success.title'
														)}
													</Typography>
													{loginDetails && (
														<Box sx={{ width: '100%' }}>
															<Typography
																variant="body1"
																color={lightTheme.palette.grey[900]}
																sx={{
																	marginBottom: '16px',
																	textAlign: 'center'
																}}
															>
																{intl.tr(
																	'wizard.phase.integrate.preview.auth.success.result'
																)}
															</Typography>
															<Box
																sx={{
																	overflow: 'scroll',
																	height: '300px',
																	borderRadius: '4px'
																}}
															>
																<CodeSnippet
																	flowId={selectedFlow.id}
																	projectId={projectId}
																	langType={LangTypeBackend}
																	value={JSON.stringify(
																		loginDetails,
																		undefined,
																		2
																	)}
																	lang={CODE_EDITOR_SUPPORTED_LANG.json}
																	withTabs={false}
																	fontSize="10px"
																/>
															</Box>
															<Typography
																variant="body1"
																color={lightTheme.palette.grey[900]}
																sx={{
																	marginBottom: '16px',
																	marginTop: '16px',
																	textAlign: 'center'
																}}
															>
																{intl.tr(
																	'wizard.phase.integrate.preview.auth.success.session'
																)}
															</Typography>
															<Box
																sx={{
																	overflow: 'scroll',
																	height: '300px',
																	borderRadius: '4px'
																}}
															>
																<CodeSnippet
																	flowId={selectedFlow.id}
																	projectId={projectId}
																	langType={LangTypeBackend}
																	value={`// Payload:\n${sessionJwtPayload}\n\n// Header:\n${sessionJwtHeader}`}
																	lang={CODE_EDITOR_SUPPORTED_LANG.json}
																	withTabs={false}
																	fontSize="10px"
																/>
															</Box>
														</Box>
													)}
												</Box>
											) : (
												!clearInProgress &&
												!runCodeLoading && (
													<Box data-testid="wizard-descope-flow" zIndex={100}>
														<DescopeFlowPreview
															projectId={projectId}
															flowId={selectedFlow.id}
															onSuccess={(res) => {
																setRunCodeClicked(true);
																setSuccessLogin(true);
																setLoginDetails(res.detail);
															}}
															onError={() => {
																setRunCodeClicked(true);
																setSuccessLogin(false);
															}}
														/>
													</Box>
												)
											))}
									</Box>
								</Grid>
							</Grid>
						</Box>
					</ThemeProvider>
					<Box textAlign="center">
						<Tooltip
							title={
								shouldDisplayRunCode
									? intl.tr(
											'wizard.phase.integrate.preview.sampleApp.disabled.tooltip'
									  )
									: undefined
							}
							placement="left"
						>
							<Typography variant="body1">
								<Link
									target="_blank"
									href={sampleApp || 'https://docs.descope.com'}
									underline="hover"
									classes={{ root: shouldDisplayRunCode ? 'disabled' : '' }}
								>
									{intl.tr('wizard.phase.integrate.preview.sampleApp')}
									<OpenInNewIcon sx={{ marginLeft: '6px' }} fontSize="small" />
								</Link>
							</Typography>
						</Tooltip>
					</Box>
				</Box>
			</Box>
		</WizardContentLayout>
	);
};

export default WizardPreviewPage;
